import { Badge } from '@/Components/ui/badge';
import { ScrollArea } from '@/Components/ui/scroll-area';
import { SheetContent } from '@/Components/ui/sheet';
import { Link } from '@inertiajs/react';
import * as Icons from 'lucide-react';
import { LucideIcon } from 'lucide-react';
import React from 'react';

interface Solution {
    title: string;
    description: string;
    icon: keyof typeof Icons;
    href: string;
    badge?: string;
}

interface QuickAction {
    title: string;
    description: string;
    icon: LucideIcon;
    onClick: () => void;
    badge?: string;
    color?: string;
}

interface MobileMenuProps {
    solutions: Solution[];
    quickActions: QuickAction[];
    navLinks: Array<{
        href: string;
        label: string;
        active?: boolean;
    }>;
}

const MobileMenu: React.FC<MobileMenuProps> = ({
    solutions,
    quickActions,
    navLinks,
}) => {
    const renderIcon = (iconName: keyof typeof Icons): JSX.Element | null => {
        const IconComponent = Icons[iconName] as React.ElementType;
        return IconComponent ? (
            <IconComponent className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
        ) : null;
    };

    return (
        <SheetContent
            side="left"
            className="w-full max-w-sm border-r border-emerald-100 bg-gradient-to-br from-white via-white/95 to-amber-50/20 p-0 backdrop-blur-lg dark:border-emerald-800/30 dark:from-gray-800 dark:via-gray-800/95 dark:to-amber-900/20"
        >
            <ScrollArea className="h-full">
                {/* Header with Vision Statement */}
                <div className="bg-gradient-to-r from-emerald-50 to-amber-50 p-6 dark:from-emerald-900/30 dark:to-amber-900/30">
                    <h2 className="bg-gradient-to-r from-emerald-600 to-amber-600 bg-clip-text text-lg font-bold text-transparent">
                        Welcome to Gaphen
                    </h2>
                    <p className="mt-2 text-sm text-emerald-700 dark:text-emerald-300">
                        Empowering sustainable agriculture for a better tomorrow
                    </p>
                </div>

                <div className="space-y-6 p-6">
                    {/* Solutions Section */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="bg-gradient-to-r from-emerald-600 to-amber-600 bg-clip-text text-lg font-semibold text-transparent">
                                Solutions
                            </h3>
                            <Badge
                                variant="secondary"
                                className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100"
                            >
                                Sustainable
                            </Badge>
                        </div>
                        <div className="grid gap-2">
                            {solutions.map((solution) => (
                                <Link
                                    key={solution.title}
                                    href={solution.href}
                                    className="group relative flex items-center space-x-4 rounded-xl border border-emerald-100 bg-white/50 p-4 transition-all hover:border-emerald-200 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-amber-50 hover:shadow-md dark:border-emerald-800/30 dark:bg-gray-800/50 dark:hover:border-emerald-700/30 dark:hover:from-emerald-900/30 dark:hover:to-amber-900/30"
                                >
                                    <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-emerald-100 transition-colors group-hover:bg-emerald-200 dark:bg-emerald-900/50 dark:group-hover:bg-emerald-800/70">
                                        {renderIcon(solution.icon)}
                                        {solution.badge && (
                                            <Badge
                                                variant="secondary"
                                                className="absolute -right-2 -top-2 bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100"
                                            >
                                                {solution.badge}
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        <h4 className="font-medium text-emerald-900 dark:text-emerald-100">
                                            {solution.title}
                                        </h4>
                                        <p className="line-clamp-2 text-sm text-emerald-700 dark:text-emerald-300">
                                            {solution.description}
                                        </p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>

                    <div className="h-px bg-gradient-to-r from-emerald-200 via-amber-200 to-emerald-200 dark:from-emerald-800/30 dark:via-amber-800/30 dark:to-emerald-800/30" />

                    {/* Quick Actions Section */}
                    <div className="space-y-4">
                        <h3 className="bg-gradient-to-r from-emerald-600 to-amber-600 bg-clip-text text-lg font-semibold text-transparent">
                            Quick Actions
                        </h3>
                        <div className="grid gap-2">
                            {quickActions.map((action) => (
                                <button
                                    key={action.title}
                                    onClick={action.onClick}
                                    className="group relative flex w-full items-center space-x-4 rounded-xl border border-emerald-100 bg-white/50 p-4 text-left transition-all hover:border-emerald-200 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-amber-50 hover:shadow-md dark:border-emerald-800/30 dark:bg-gray-800/50 dark:hover:border-emerald-700/30 dark:hover:from-emerald-900/30 dark:hover:to-amber-900/30"
                                >
                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-emerald-100 transition-colors group-hover:bg-emerald-200 dark:bg-emerald-900/50 dark:group-hover:bg-emerald-800/70">
                                        <action.icon
                                            className={`h-6 w-6 ${action.color || 'text-emerald-600 dark:text-emerald-400'}`}
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <div className="flex items-center justify-between">
                                            <h4 className="font-medium text-emerald-900 dark:text-emerald-100">
                                                {action.title}
                                            </h4>
                                            {action.badge && (
                                                <Badge
                                                    variant="secondary"
                                                    className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100"
                                                >
                                                    {action.badge}
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="text-sm text-emerald-700 dark:text-emerald-300">
                                            {action.description}
                                        </p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="h-px bg-gradient-to-r from-emerald-200 via-amber-200 to-emerald-200 dark:from-emerald-800/30 dark:via-amber-800/30 dark:to-emerald-800/30" />

                    {/* Navigation Links */}
                    <div className="space-y-4">
                        <h3 className="bg-gradient-to-r from-emerald-600 to-amber-600 bg-clip-text text-lg font-semibold text-transparent">
                            Menu
                        </h3>
                        <nav className="grid gap-2">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    className={`flex items-center rounded-lg px-4 py-3 text-sm font-medium transition-all hover:bg-gradient-to-r hover:from-emerald-50 hover:to-amber-50 dark:hover:from-emerald-900/30 dark:hover:to-amber-900/30 ${
                                        link.active
                                            ? 'bg-gradient-to-r from-emerald-100 to-amber-100 text-emerald-900 dark:from-emerald-900/50 dark:to-amber-900/50 dark:text-emerald-100'
                                            : 'text-emerald-700 hover:text-emerald-900 dark:text-emerald-300 dark:hover:text-emerald-100'
                                    }`}
                                >
                                    {link.label}
                                </Link>
                            ))}
                        </nav>
                    </div>

                    {/* Footer Section - Vision Statement */}
                    <div className="mt-6">
                        <div className="rounded-xl bg-gradient-to-r from-emerald-50 to-amber-50 p-4 dark:from-emerald-900/30 dark:to-amber-900/30">
                            <blockquote className="space-y-2">
                                <p className="text-sm italic text-emerald-800 dark:text-emerald-200">
                                    "Agriculture in harmony with nature,
                                    empowering farmers for a sustainable
                                    future."
                                </p>
                                <footer className="text-xs text-emerald-600 dark:text-emerald-400">
                                    — Gaphen Vision
                                </footer>
                            </blockquote>
                        </div>
                    </div>
                </div>
            </ScrollArea>
        </SheetContent>
    );
};

export default MobileMenu;
